/**
 * AuthContext — JWT auth state management
 * Reads/writes `authToken` (apiClient already uses this key)
 * Exposes: user, login(), register(), logout(), updateUser(), loading
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../lib/apiClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);  // checking stored token on mount

  // On mount, validate stored token and sync charity_bn to localStorage
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) { setLoading(false); return; }
    apiClient.get('/auth/me')
      .then(r => {
        const u = r.data;
        setUser(u);
        // Sync DB-stored BN to localStorage (covers new-device logins)
        if (u.charity_bn) {
          localStorage.setItem('impactplexus_charity_bn', u.charity_bn);
        }
      })
      .catch(() => localStorage.removeItem('authToken'))
      .finally(() => setLoading(false));
  }, []);

  const _storeAuth = (token, userData) => {
    localStorage.setItem('authToken', token);
    if (userData.charity_bn) {
      localStorage.setItem('impactplexus_charity_bn', userData.charity_bn);
    }
    setUser(userData);
  };

  const register = useCallback(async ({ email, password, full_name, charity_bn }) => {
    const { data } = await apiClient.post('/auth/register', { email, password, full_name, charity_bn });
    _storeAuth(data.token, data.user);
    return data.user;
  }, []);

  const login = useCallback(async ({ email, password }) => {
    const { data } = await apiClient.post('/auth/login', { email, password });
    _storeAuth(data.token, data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('impactplexus_charity_bn');
    localStorage.removeItem('impactplexus_org_name');
    setUser(null);
  }, []);

  // Call after PATCH /auth/me to keep React state in sync with DB
  const updateUser = useCallback((updatedFields) => {
    setUser(prev => {
      const next = { ...prev, ...updatedFields };
      if (next.charity_bn) {
        localStorage.setItem('impactplexus_charity_bn', next.charity_bn);
      }
      return next;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
